import { INewOrderFormValues } from "../../types/user";

export type DeliveryAddress = Pick<
  INewOrderFormValues,
  "street" | "streetNumber" | "city" | "state"
>;

export interface GeocodedAddress {
  id: string;
  label: string;
  latitude: number;
  longitude: number;
  street: string;
  streetNumber: string;
  city: string;
  state: string;
  isBuilding: boolean;
}

const endpoint =
  process.env.REACT_APP_GEOCODING_URL || "https://photon.komoot.io/api/";
const cache = new Map<
  string,
  { expires: number; results: GeocodedAddress[] }
>();
let lastRequestAt = 0;

export const addressQuery = (address: DeliveryAddress): string =>
  [
    [address.street?.trim(), address.streetNumber?.trim()]
      .filter(Boolean)
      .join(" "),
    address.city?.trim(),
    address.state?.trim(),
  ]
    .filter(Boolean)
    .join(", ");

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const stringValue = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const parseFeature = (feature: unknown): GeocodedAddress | null => {
  if (
    !isRecord(feature) ||
    !isRecord(feature.geometry) ||
    feature.geometry.type !== "Point" ||
    !Array.isArray(feature.geometry.coordinates) ||
    !isRecord(feature.properties)
  ) {
    return null;
  }

  const [longitude, latitude] = feature.geometry.coordinates;
  const { properties } = feature;
  if (
    typeof latitude !== "number" ||
    typeof longitude !== "number" ||
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude) ||
    Math.abs(latitude) > 90 ||
    Math.abs(longitude) > 180 ||
    stringValue(properties.countrycode).toUpperCase() !== "PL"
  ) {
    return null;
  }

  const name = stringValue(properties.name);
  const street =
    stringValue(properties.street) ||
    (properties.type === "street" ? name : "");
  const streetNumber = stringValue(properties.housenumber);
  const city = stringValue(properties.city);
  const state = stringValue(properties.state).replace(/^województwo\s+/i, "");
  const streetLine = [street, streetNumber].filter(Boolean).join(" ");
  const label = [
    name !== street ? name : "",
    streetLine,
    [stringValue(properties.postcode), city].filter(Boolean).join(" "),
    state,
  ]
    .filter(Boolean)
    .join(", ");

  if (!label) return null;

  return {
    id: `${latitude},${longitude}:${label}`,
    label,
    latitude,
    longitude,
    street,
    streetNumber,
    city,
    state,
    isBuilding: Boolean(street && streetNumber && city),
  };
};

export const searchDeliveryAddress = async (
  query: string,
  signal: AbortSignal
): Promise<GeocodedAddress[]> => {
  const cached = cache.get(query);
  if (cached && cached.expires > Date.now()) return cached.results;

  if (Date.now() - lastRequestAt < 1000) {
    throw new Error("Odczekaj chwilę przed kolejnym wyszukiwaniem.");
  }

  const url = new URL(endpoint, window.location.origin);
  url.searchParams.set("q", query);
  url.searchParams.set("limit", "5");
  url.searchParams.set("countrycode", "PL");
  lastRequestAt = Date.now();

  // A separate request prevents sending the panel's token or company headers.
  const response = await fetch(url.toString(), {
    signal,
    credentials: "omit",
    referrerPolicy: "no-referrer",
  }).catch(() => {
    throw new Error(
      "Nie udało się połączyć z wyszukiwarką adresów. Spróbuj ponownie."
    );
  });
  if (!response.ok) {
    throw new Error("Wyszukiwanie adresu jest chwilowo niedostępne.");
  }

  const data: unknown = await response.json().catch(() => {
    throw new Error("Nie udało się odczytać wyników wyszukiwania.");
  });
  if (!isRecord(data) || !Array.isArray(data.features)) {
    throw new Error("Nie udało się odczytać wyników wyszukiwania.");
  }

  const results = data.features
    .map(parseFeature)
    .filter((result): result is GeocodedAddress => result !== null)
    .filter(
      (result, index, all) =>
        all.findIndex((candidate) => candidate.id === result.id) === index
    )
    .slice(0, 5);

  if (!signal.aborted) {
    if (cache.size >= 50) cache.delete(cache.keys().next().value);
    cache.set(query, { results, expires: Date.now() + 5 * 60 * 1000 });
  }
  return results;
};

export const mapUrls = (
  location: GeocodedAddress
): { embed: string; external: string } => {
  const { latitude, longitude, isBuilding } = location;
  const offset = isBuilding ? 0.003 : 0.015;
  const bbox = [
    Math.max(-180, longitude - offset),
    Math.max(-90, latitude - offset),
    Math.min(180, longitude + offset),
    Math.min(90, latitude + offset),
  ].join(",");
  const params = new URLSearchParams({
    bbox,
    layer: "mapnik",
    marker: `${latitude},${longitude}`,
  });
  return {
    embed: `https://www.openstreetmap.org/export/embed.html?${params.toString()}`,
    external: `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=${
      isBuilding ? 17 : 14
    }/${latitude}/${longitude}`,
  };
};
